/**
 * @file Hook de suivi du signal « empreinte bancaire » dans le formData du parcours.
 * @module packages.common.components.table-reservation.hooks.usePaymentSignalTracker
 *
 * Déduit automatiquement les sous-états observables depuis le formData (écran
 * de carte affiché, carte saisie) et expose deux enregistreurs pour ce que
 * seuls les handlers connaissent (validation finale tentée, erreur d'une étape
 * technique). Le signal vit dans `formData.paymentSignal` : il est ainsi
 * embarqué par `useReservationDraftSync` dans le brouillon backend sans canal
 * supplémentaire. Aucun effet sur le parcours lui-même.
 */
import {useCallback, useEffect, useRef} from 'react';
import {BASE_STEPS} from '../constants/steps';
import {
  PAYMENT_SIGNAL_STATES,
  advancePaymentState,
  markPaymentAttempt,
  withPaymentError,
} from '../utils/paymentSignal';

/** Clé du signal dans le formData */
const SIGNAL_FIELD = 'paymentSignal';

/**
 * Suit le signal empreinte du parcours courant.
 *
 * @param {Object} params - Paramètres du hook
 * @param {Object} params.formData - État du formulaire (paymentSignal, paymentRequired, paymentMethodId)
 * @param {string} params.currentStep - Étape logique courante (BASE_STEPS)
 * @param {Function} params.updateFormData - Setter de champ du formulaire
 * @return {{recordPaymentError: Function, recordPaymentAttempt: Function, recordCardValidated: Function}}
 *     Enregistreurs stables : `recordPaymentError(stage, error)`, `recordPaymentAttempt()`
 *     et `recordCardValidated()`
 */
export function usePaymentSignalTracker({formData, currentStep, updateFormData}) {
  const {paymentSignal, paymentRequired, paymentMethodId} = formData;

  // Les enregistreurs sont appelés depuis des callbacks asynchrones : la ref
  // garantit qu'ils partent toujours du signal le plus récent
  const signalRef = useRef(paymentSignal);
  signalRef.current = paymentSignal;

  const commit = useCallback((next) => {
    if (next === signalRef.current) return;
    signalRef.current = next;
    updateFormData(SIGNAL_FIELD, next);
  }, [updateFormData]);

  // Écran de carte affiché
  useEffect(() => {
    if (currentStep !== BASE_STEPS.PAYMENT || !paymentRequired) return;
    commit(advancePaymentState(signalRef.current, PAYMENT_SIGNAL_STATES.DISPLAYED));
  }, [currentStep, paymentRequired, commit]);

  // Carte complète validée par Stripe Elements
  useEffect(() => {
    if (!paymentMethodId) return;
    commit(advancePaymentState(signalRef.current, PAYMENT_SIGNAL_STATES.CARD_ENTERED));
  }, [paymentMethodId, commit]);

  const recordPaymentError = useCallback((stage, error) => {
    commit(withPaymentError(signalRef.current, stage, error));
  }, [commit]);

  const recordPaymentAttempt = useCallback(() => {
    commit(markPaymentAttempt(signalRef.current));
  }, [commit]);

  // Carte acceptée par Stripe : ce qui échoue ensuite n'est plus bancaire
  const recordCardValidated = useCallback(() => {
    commit(advancePaymentState(signalRef.current, PAYMENT_SIGNAL_STATES.CARD_VALIDATED));
  }, [commit]);

  return {recordPaymentError, recordPaymentAttempt, recordCardValidated};
}
